import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './Chat.css';

function Chat() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const [starters, setStarters] = useState([]);
  const [sharedIdentities, setSharedIdentities] = useState([]);
  const messagesEndRef = useRef(null);
  const pollInterval = useRef(null);

  useEffect(() => {
    initializeChat();
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [userId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initializeChat = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      
      console.log('🔍 Initializing chat with userId:', userId);
      console.log('🔑 Token exists:', !!token);
      
      // Get or create conversation
      console.log('📞 Getting conversation...');
      const convResponse = await axios.get(`/api/messages/conversation/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ Conversation response:', convResponse.data);
      const convId = convResponse.data.conversationId;
      setConversationId(convId);
      
      // Get user info
      console.log('📞 Getting user info...');
      const userResponse = await axios.get(`/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('✅ User info:', userResponse.data);
      setOtherUser(userResponse.data);
      
      // Load messages
      console.log('📞 Loading messages...');
      await loadMessages(convId);
      
      // Mark messages as read
      await markAsRead(convId);

      // Load conversation starters
      try {
        const startersResponse = await axios.get(`/api/messages/conversation/${userId}/starters`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStarters(startersResponse.data.starters || []);
        setSharedIdentities(startersResponse.data.sharedIdentities || []);
      } catch (err) {
        console.log('Starters not available:', err.message);
      }
      
      // Start polling for new messages
      pollInterval.current = setInterval(() => {
        loadMessages(convId, true);
      }, 3000);
      
    } catch (error) {
      console.error('❌ Error initializing chat:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      if (error.response?.status === 403) {
        alert('You can only message connected users');
        navigate('/connections');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (convId, silent = false) => {
    try {
      const token = localStorage.getItem('accessToken');
      console.log(`📞 Loading messages for conversation ${convId}...`);
      const response = await axios.get(`/api/messages/${convId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ Messages loaded:', response.data.messages.length, 'messages');
      console.log('Messages:', response.data.messages);
      setMessages(response.data.messages);
      
      if (!silent) {
        await markAsRead(convId);
      }
    } catch (error) {
      if (!silent) {
        console.error('❌ Error loading messages:', error);
        console.error('Error response:', error.response?.data);
      }
    }
  };

  const markAsRead = async (convId) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(`/api/messages/${convId}/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !conversationId) return;
    
    setSending(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`/api/messages/${conversationId}`, {
        text: newMessage.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages([...messages, response.data.message]);
      setNewMessage('');
      setStarters([]); // Hide starters after first message
      scrollToBottom();
      
    } catch (error) {
      console.error('Error sending message:', error);
      const errMsg = error.response?.data?.error || 'Failed to send message';
      alert(errMsg);
    } finally {
      setSending(false);
    }
  };

  const handleStarterClick = (starterText) => {
    setNewMessage(starterText);
  };

  if (loading) {
    return (
      <div className="chat-container">
        <div className="loading">Loading chat...</div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <div className="chat-header">
        <button onClick={() => navigate('/messages')} className="back-btn">
          ← Back
        </button>
        <div className="chat-user-info">
          <div className="user-avatar">
            {otherUser?.photo_url ? '📷' : '👤'}
          </div>
          <div>
            <h3>{otherUser?.first_name} {otherUser?.last_name}</h3>
            <p className="user-status">Connected</p>
          </div>
        </div>
      </div>

      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="empty-chat">
            <p>👋 Start the conversation!</p>
            <p className="hint">Send a message to {otherUser?.first_name}</p>
            {sharedIdentities.length > 0 && (
              <div className="shared-identities-hint" style={{ margin: '8px 0', fontSize: '13px', color: '#7c3aed' }}>
                You share: {sharedIdentities.map(i => `${i.icon || ''} ${i.name}`).join(', ')}
              </div>
            )}
            {starters.length > 0 && (
              <div className="conversation-starters" style={{ marginTop: '12px' }}>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>Try a conversation starter:</p>
                {starters.map((starter, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleStarterClick(starter)}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 14px',
                      marginBottom: '8px',
                      background: '#f3f0ff',
                      border: '1px solid #ddd6fe',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '14px',
                      color: '#4c1d95',
                      transition: 'background 0.2s'
                    }}
                    onMouseOver={e => e.target.style.background = '#ede9fe'}
                    onMouseOut={e => e.target.style.background = '#f3f0ff'}
                  >
                    💬 {starter}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          messages.map((msg) => (
            <div key={msg.id} className={`message ${msg.isMine ? 'mine' : 'theirs'}`}>
              <div className="message-bubble">
                <p>{msg.text}</p>
                <span className="message-time">
                  {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {msg.isMine && (msg.isRead ? ' ✓✓' : ' ✓')}
                </span>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSend} className="message-input-container">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
          className="message-input"
          disabled={sending}
          maxLength={5000}
        />
        <button 
          type="submit" 
          className="send-btn"
          disabled={!newMessage.trim() || sending}
        >
          {sending ? '...' : '📤'}
        </button>
      </form>
    </div>
  );
}

export default Chat;
